import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  KeyRound, Plus, Trash2, Copy, ExternalLink, Loader2, ShieldCheck,
  Clock, X, ScanLine, AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const copy = {
  fr: {
    title: 'Tokens Bénévoles',
    subtitle: 'Créez des liens de pointage à durée limitée pour vos bénévoles à la porte. Aucun mot de passe requis.',
    create: 'Créer un nouveau token',
    label: 'Nom du bénévole / poste',
    labelHint: "Ex. « Marie - Porte d'entrée »",
    expires: 'Durée de validité',
    h6: '6 heures', h12: '12 heures', h24: '24 heures', h48: '48 heures', h72: '3 jours', h168: '7 jours',
    createBtn: 'Créer le lien',
    creating: 'Création…',
    nothing: 'Aucun token actif. Créez-en un pour démarrer.',
    active: 'Actif',
    revoked: 'Révoqué',
    expired: 'Expiré',
    used: 'pointages',
    revoke: 'Révoquer',
    confirmRevoke: 'Révoquer ce lien ? Le bénévole perdra l’accès immédiatement.',
    showQr: 'Afficher QR',
    copyLink: 'Copier le lien',
    copied: 'Copié !',
    openLink: 'Ouvrir',
    expiresAt: 'Expire',
    createdBy: 'Créé par',
    qrTitle: 'QR du lien bénévole',
    qrHint: 'Le bénévole scanne ce QR avec son téléphone pour ouvrir directement la page de pointage.',
    close: 'Fermer',
    error: 'Une erreur est survenue. Réessayez.',
  },
  en: {
    title: 'Staff Tokens',
    subtitle: 'Mint short-lived check-in links for door volunteers. No admin password required.',
    create: 'Create a new token',
    label: 'Volunteer name / station',
    labelHint: 'e.g., "Marie · Front Door"',
    expires: 'Valid for',
    h6: '6 hours', h12: '12 hours', h24: '24 hours', h48: '48 hours', h72: '3 days', h168: '7 days',
    createBtn: 'Create link',
    creating: 'Creating…',
    nothing: 'No tokens yet. Create one to get started.',
    active: 'Active',
    revoked: 'Revoked',
    expired: 'Expired',
    used: 'check-ins',
    revoke: 'Revoke',
    confirmRevoke: 'Revoke this link? The volunteer loses access immediately.',
    showQr: 'Show QR',
    copyLink: 'Copy link',
    copied: 'Copied!',
    openLink: 'Open',
    expiresAt: 'Expires',
    createdBy: 'Created by',
    qrTitle: 'Staff link QR',
    qrHint: 'Have the volunteer scan this QR with their phone to open the check-in page directly.',
    close: 'Close',
    error: 'Something went wrong. Please retry.',
  },
};

const HOUR_OPTIONS = [
  { v: 6, k: 'h6' }, { v: 12, k: 'h12' }, { v: 24, k: 'h24' },
  { v: 48, k: 'h48' }, { v: 72, k: 'h72' }, { v: 168, k: 'h168' },
];

export default function StaffTokensPanel({ token, language = 'fr' }) {
  const t = copy[language] || copy.fr;
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ label: '', expires_in_hours: 24 });
  const [qrTokenId, setQrTokenId] = useState(null);
  const [copiedId, setCopiedId] = useState('');

  const fetchTokens = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/staff-tokens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTokens(res.data || []);
    } catch (_e) { /* ignore */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchTokens(); }, [fetchTokens]);

  const buildLink = (rawToken) => `${window.location.origin}/staff-checkin?token=${rawToken}`;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return;
    setCreating(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/staff-tokens`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm({ label: '', expires_in_hours: 24 });
      fetchTokens();
    } catch (err) {
      setError(err?.response?.data?.detail || t.error);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm(t.confirmRevoke)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/staff-tokens/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchTokens();
    } catch (_e) { /* ignore */ }
  };

  const handleCopy = async (id, link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      setTimeout(() => setCopiedId(''), 1500);
    } catch (_e) { /* ignore */ }
  };

  const qrToken = tokens.find((tk) => tk.id === qrTokenId);

  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-5 shadow-sm" data-testid="staff-tokens-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
          <KeyRound className="w-5 h-5 text-[#CD0000]" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold text-[#1D1D1D]">{t.title}</h3>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{t.subtitle}</p>
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={handleCreate} className="bg-gray-50 rounded-xl p-3 space-y-2.5 mb-4" data-testid="staff-token-create-form">
        <div className="grid sm:grid-cols-[1fr,140px] gap-2">
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{t.label}</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={t.labelHint}
              data-testid="staff-token-label-input"
              maxLength={80}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#CD0000]"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{t.expires}</label>
            <select
              value={form.expires_in_hours}
              onChange={(e) => setForm({ ...form, expires_in_hours: Number(e.target.value) })}
              data-testid="staff-token-expiry-select"
              className="w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-[#CD0000]"
            >
              {HOUR_OPTIONS.map((opt) => (
                <option key={opt.v} value={opt.v}>{t[opt.k]}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          type="submit"
          disabled={creating || !form.label.trim()}
          data-testid="staff-token-create-submit"
          className="w-full bg-[#1D1D1D] hover:bg-black disabled:opacity-50 text-white font-bold text-sm px-4 py-2.5 rounded-lg transition flex items-center justify-center gap-2"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {creating ? t.creating : t.createBtn}
        </button>
        {error && <p className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</p>}
      </form>

      {/* Token list */}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : tokens.length === 0 ? (
        <p className="text-xs text-gray-400 italic text-center py-6">{t.nothing}</p>
      ) : (
        <ul className="space-y-2.5" data-testid="staff-tokens-list">
          {tokens.map((tk) => {
            const link = buildLink(tk.token);
            const status = tk.revoked ? 'revoked' : (tk.expired ? 'expired' : 'active');
            const statusStyle = {
              active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
              revoked: 'bg-gray-100 text-gray-500 border-gray-200',
              expired: 'bg-amber-50 text-amber-700 border-amber-200',
            }[status];
            return (
              <li key={tk.id} className="border border-gray-100 rounded-xl p-3 bg-white hover:shadow-sm transition" data-testid={`staff-token-row-${tk.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-extrabold text-[#1D1D1D] truncate">{tk.label}</p>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusStyle}`}>
                        {t[status]}
                      </span>
                      {tk.checkin_count > 0 && (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded">
                          {tk.checkin_count} {t.used}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {t.expiresAt}: {new Date(tk.expires_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                      {tk.created_by_name && <> · {t.createdBy} {tk.created_by_name}</>}
                    </p>
                    {status === 'active' && (
                      <p className="text-[10px] font-mono text-gray-400 mt-1 truncate">{link}</p>
                    )}
                  </div>
                </div>

                {status === 'active' && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                    <button
                      type="button"
                      onClick={() => handleCopy(tk.id, link)}
                      data-testid={`staff-token-copy-${tk.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md bg-red-50 text-[#CD0000] hover:bg-red-100 transition"
                    >
                      <Copy className="w-3 h-3" />
                      {copiedId === tk.id ? t.copied : t.copyLink}
                    </button>
                    <button
                      type="button"
                      onClick={() => setQrTokenId(tk.id)}
                      data-testid={`staff-token-qr-${tk.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                    >
                      <ScanLine className="w-3 h-3" />
                      {t.showQr}
                    </button>
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`staff-token-open-${tk.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {t.openLink}
                    </a>
                    <button
                      type="button"
                      onClick={() => handleRevoke(tk.id)}
                      data-testid={`staff-token-revoke-${tk.id}`}
                      className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 transition"
                    >
                      <Trash2 className="w-3 h-3" />
                      {t.revoke}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* QR Modal */}
      {qrToken && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setQrTokenId(null)}
          data-testid="staff-token-qr-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-base font-extrabold text-[#1D1D1D] flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#CD0000]" />
                  {t.qrTitle}
                </h3>
                <p className="text-[11px] text-gray-500 mt-0.5">{qrToken.label}</p>
              </div>
              <button
                type="button"
                onClick={() => setQrTokenId(null)}
                data-testid="staff-token-qr-close"
                className="p-1 rounded-lg text-gray-400 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex justify-center bg-white p-4 border border-gray-100 rounded-xl">
              <QRCodeSVG value={buildLink(qrToken.token)} size={220} level="M" fgColor="#1D1D1D" />
            </div>
            <p className="text-[11px] text-gray-500 mt-3 leading-relaxed text-center">{t.qrHint}</p>
            <p className="text-[10px] font-mono text-gray-400 mt-3 break-all">{buildLink(qrToken.token)}</p>
            <button
              type="button"
              onClick={() => setQrTokenId(null)}
              className="mt-4 w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm py-2.5 rounded-xl transition"
            >
              {t.close}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
